import { useCallback, useEffect, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { timeframeRowKey } from '@renderer/overlay/Sidepanel/utils/group-by-timeframe'
import { useFindInPageRangeExtractor } from '../../hooks/use-find-in-page-mount-all'
import { useViewContext } from '../../view-context'
import { View } from '../../components/view'
import { ViewGroupLabel } from '../../components/view-group-label'
import { ReminderCell } from '../../components/reminders/reminders-cell'
import { useRemindersSurfacePresence } from '../../hooks/reminder-linger'
import { RemindersViewHeader } from './nested/header'
import { useRemindersViewModel, type RemindersViewContext } from './use-reminders-view-model'

/** Full-list View for reminders, entered from the Home section header. */
export function RemindersView() {
  // Every tab but All drops resolved reminders, so this view holds
  // just-completed ones open while it's on screen (`reminder-linger`).
  useRemindersSurfacePresence()
  const { context } = useViewContext<RemindersViewContext>()
  const model = useRemindersViewModel(context)
  // Already grouped — the model decides what a filter means, this just draws it.
  const { rows, focusReminderId, createdTick } = model

  // The virtualizer measures against the body's scroll element, which
  // View.Body owns — take it as a value on mount.
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
  const captureScrollElement = useCallback((element: HTMLDivElement | null) => setScrollElement(element), [])
  const rangeExtractor = useFindInPageRangeExtractor()

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollElement,
    // Collapsed rows are a single title line; the virtualizer re-measures each
    // row on mount, so this only has to be close enough for the first frame.
    estimateSize: (index) => (rows[index]?.kind === 'label' ? 26 : 44),
    overscan: 6,
    rangeExtractor,
    getItemKey: (index) => {
      const row = rows[index]
      return row ? timeframeRowKey(row, (item) => item.id) : index
    },
  })

  // Ensure the surfaced reminder (pinned to the top) is visible when opened from a toast.
  useEffect(() => {
    if (focusReminderId) virtualizer.scrollToIndex(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusReminderId])

  useEffect(() => {
    if (createdTick === 0) {
      return
    }
    virtualizer.scrollToIndex(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdTick])

  return (
    <View>
      <RemindersViewHeader model={model} />

      <View.Body bottomInset="list" onScrollElement={captureScrollElement}>
        {rows.length === 0 ? (
          <View.Empty searchQuery={model.searchQuery}>No items yet</View.Empty>
        ) : (
          <div className="relative w-full" style={{ height: virtualizer.getTotalSize() }}>
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index]
              if (!row) return null
              return (
                <div
                  key={virtualRow.key}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  className="absolute left-0 top-0 w-full pb-[2px]"
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  {row.kind === 'label' ? (
                    <ViewGroupLabel isFirst={row.isFirst}>{row.label}</ViewGroupLabel>
                  ) : (
                    <ReminderCell item={row.item} variant="row" />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </View.Body>
    </View>
  )
}
